/**
 * MED-1389: bookings made through a group booking link that did not attach
 * to a session. Each one holds the coach's time and sent an invite, but no
 * session shows it, so staff see it here and can cancel it.
 *
 * Shown in the Sessions tab under 1:1 Coaching, in the same section and row
 * style. Staff only: the API refuses everyone else, and the section does not
 * render during impersonation.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Calendar } from 'react-feather';
import {
  cohortPublicBookingsApi,
  type UnattachedBookingOutcome,
  type UnattachedPublicBooking,
} from '../../../../services/api/cohortPublicBookings';
import { formatDateTime } from '../../../../utils/formatters';
import { ParticipantAvatar } from '../../../../components/common/ParticipantAvatar';
import { GroupPill } from './OneOnOneCoachingSection';
import styles from './UnattachedBookingsSection.module.css';

const OUTCOME_LABELS: Record<UnattachedBookingOutcome, string> = {
  unknown_email: 'Email not recognized',
  not_in_group: 'Not in this group',
  already_scheduled: 'Session already booked',
  no_unscheduled_session: 'No open session',
};

const OUTCOME_HINTS: Record<UnattachedBookingOutcome, string> = {
  unknown_email: 'No participant uses this email address.',
  not_in_group: 'This person is not an active member of the group.',
  already_scheduled: 'The session for this booking was already booked.',
  no_unscheduled_session: 'This participant has no open session to fill.',
};

function nameParts(name: string): { first: string; last: string } {
  const [first = '', ...rest] = name.trim().split(/\s+/);
  return { first, last: rest.join(' ') };
}

interface UnattachedBookingsSectionProps {
  cohortId: string;
  /** Render only when there is at least one booking (the All tab). */
  hideWhenEmpty?: boolean;
}

export function UnattachedBookingsSection({
  cohortId,
  hideWhenEmpty = false,
}: UnattachedBookingsSectionProps) {
  const [bookings, setBookings] = useState<UnattachedPublicBooking[] | null>(
    null
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  // A fast double click must not open a second confirm.
  const confirmOpen = useRef(false);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setBookings(await cohortPublicBookingsApi.listUnattached(cohortId));
    } catch {
      setLoadFailed(true);
    }
  }, [cohortId]);

  useEffect(() => {
    setBookings(null);
    void load();
  }, [load]);

  const cancelBooking = async (booking: UnattachedPublicBooking) => {
    setCancelingId(booking.id);
    try {
      await cohortPublicBookingsApi.cancel(cohortId, booking.id);
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Booking canceled',
        message: `${booking.booker_name} and the coach get a cancellation email.`,
        color: 'green',
      });
    } catch (error) {
      // The booking is on a Calendly account the coach no longer has
      // connected, or the coach has no Calendly connection at all. A retry
      // never works: offer to take it off the list. The server says which.
      if ((error as { code?: string }).code === 'calendly_booking_not_reachable') {
        confirmRemove(booking, (error as Error).message);
        return;
      }
      // A 409 means the booking changed (attached or canceled): show it now.
      void load();
      notifications.show({
        title: 'Booking not canceled',
        message:
          error instanceof Error
            ? error.message
            : 'We could not cancel this booking. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setCancelingId(null);
    }
  };

  const removeBooking = async (booking: UnattachedPublicBooking) => {
    setCancelingId(booking.id);
    try {
      await cohortPublicBookingsApi.remove(cohortId, booking.id);
      setBookings(current =>
        current ? current.filter(b => b.id !== booking.id) : current
      );
      notifications.show({
        title: 'Booking removed',
        message: 'It is off this list. Nobody got an email.',
        color: 'green',
      });
    } catch (error) {
      void load();
      notifications.show({
        title: 'Booking not removed',
        message:
          error instanceof Error
            ? error.message
            : 'We could not remove this booking. Try again in a few minutes.',
        color: 'red',
      });
    } finally {
      setCancelingId(null);
    }
  };

  const confirmRemove = (booking: UnattachedPublicBooking, reason: string) => {
    if (confirmOpen.current) return;
    confirmOpen.current = true;
    modals.openConfirmModal({
      title: 'Medley cannot cancel this in Calendly',
      centered: true,
      children: (
        <Box>
          <Text size="sm">{reason} Remove it from this list?</Text>
          <Text size="sm" c="dimmed" mt="sm">
            Nobody gets an email. If the event is still in a Calendly account,
            the coach must cancel it there.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Remove from list', cancel: 'Keep on list' },
      confirmProps: { color: 'red' },
      onConfirm: () => void removeBooking(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  const confirmCancel = (booking: UnattachedPublicBooking) => {
    if (cancelingId !== null || confirmOpen.current) return;
    confirmOpen.current = true;
    const vendor =
      booking.booked_with === 'google_calendar'
        ? 'The Google Calendar invite is deleted'
        : 'Calendly cancels the event';
    modals.openConfirmModal({
      title: 'Cancel booking',
      centered: true,
      children: (
        <Box>
          <Text size="sm">
            Cancel the {booking.touchpoint_label} that{' '}
            <strong>{booking.booker_name}</strong> booked for{' '}
            {formatDateTime(booking.scheduled_start)}?
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            {vendor}, and the booker and the coach get a cancellation email. The
            coach&apos;s time opens again. This cannot be undone.
          </Text>
        </Box>
      ),
      labels: { confirm: 'Cancel booking', cancel: 'Keep booking' },
      confirmProps: { color: 'red' },
      onConfirm: () => void cancelBooking(booking),
      onClose: () => {
        confirmOpen.current = false;
      },
    });
  };

  if (hideWhenEmpty && (!bookings || bookings.length === 0)) return null;

  const count = bookings?.length ?? 0;
  const subtitle =
    count === 0
      ? 'Booked through a booking link, not linked to a session'
      : `${count} upcoming booking${count !== 1 ? 's' : ''} not linked to a session`;

  return (
    <Box className={styles.section}>
      <Group gap="sm" wrap="nowrap" className={styles.header}>
        <ThemeIcon color="gray" size={40} radius="md" variant="filled">
          <Calendar size={20} />
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="16px">
            Unlinked bookings
          </Text>
          <Text size="13px" c="dimmed" mt={4}>
            {subtitle}
          </Text>
        </Box>
      </Group>

      <Box className={styles.list}>
        {loadFailed ? (
          <Group justify="center" gap="sm" p="xl">
            <Text size="sm" c="dimmed">
              We could not load these bookings.
            </Text>
            <Button variant="default" size="xs" radius="sm" onClick={load}>
              Try again
            </Button>
          </Group>
        ) : bookings === null ? (
          <Center p="xl">
            <Loader size="sm" />
          </Center>
        ) : bookings.length === 0 ? (
          <Text size="sm" c="dimmed" p="xl" ta="center">
            No unlinked bookings. Every upcoming booking made through a booking
            link is on a participant&apos;s session.
          </Text>
        ) : (
          bookings.map(booking => {
            const { first, last } = nameParts(booking.booker_name);
            return (
              <Box key={booking.id} className={styles.row}>
                <Box className={styles.who}>
                  <ParticipantAvatar
                    firstName={first}
                    lastName={last}
                    size={40}
                  />
                  <Box className={styles.whoText}>
                    <Box className={styles.nameLine}>
                      <Text
                        fw={600}
                        size="sm"
                        className={styles.ellipsis}
                        title={booking.booker_name}
                      >
                        {booking.booker_name}
                      </Text>
                    </Box>
                    <Text
                      size="12px"
                      c="dimmed"
                      className={styles.ellipsis}
                      title={booking.booker_email}
                    >
                      {booking.booker_email}
                    </Text>
                  </Box>
                </Box>

                <Box className={styles.when}>
                  <Text size="sm" fw={500}>
                    {formatDateTime(booking.scheduled_start)}
                  </Text>
                  <Text size="12px" c="dimmed">
                    {booking.touchpoint_label}
                    {booking.coach_name ? ` with ${booking.coach_name}` : ''}
                    {booking.booked_with === 'google_calendar'
                      ? ' · Google Calendar invite'
                      : ' · Calendly'}
                  </Text>
                  {/* The group whose link was used, not the booker's group. */}
                  <Box mt={4} style={{ display: 'flex', minWidth: 0 }}>
                    <GroupPill
                      groupName={booking.group_name}
                      groupId={booking.group_id}
                      label={`Via ${booking.group_name} link`}
                      title={`Booked through the booking link of ${booking.group_name}`}
                    />
                  </Box>
                </Box>

                <Box
                  className={styles.reason}
                  title={OUTCOME_HINTS[booking.attach_outcome]}
                >
                  <Box className={styles.dot} aria-hidden />
                  <Box style={{ minWidth: 0 }}>
                    <Text size="13px">
                      {OUTCOME_LABELS[booking.attach_outcome]}
                    </Text>
                    {booking.status === 'unconfirmed' && (
                      <Text size="12px" c="dimmed">
                        Calendly did not confirm it
                      </Text>
                    )}
                  </Box>
                </Box>

                <Box className={styles.action}>
                  <Button
                    variant="default"
                    size="xs"
                    radius="sm"
                    color="red"
                    loading={cancelingId === booking.id}
                    disabled={
                      cancelingId !== null && cancelingId !== booking.id
                    }
                    onClick={() => confirmCancel(booking)}
                  >
                    Cancel booking
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}

export default UnattachedBookingsSection;
